import { SearchBackgroundKeywordsCombobox } from '@components/app/panels/right-panel/combo-box'
import {
  TRI_STATE_FILTER_OPTIONS,
  type TrayFilterSegmentOption,
  TrayFilterSegmentRow,
  TraySearchSuggestInput,
} from '@components/app/panels/right-panel/tray'
import type { BackgroundsSizeFilter } from '@/lib/backgrounds/types/backgrounds-size-filter'
import type { BackgroundsPlacedFilter } from '@/lib/backgrounds/backgrounds-panel-settings'
import { cn } from '@/utils'

// Wider than the shared 136px default — "Spread" needs more room than the Any/Yes/No-style
// labels the default width was sized for. Matched on both rows so their edges still line up.
// Kept well under the panel's ~236px available row width (300px panel minus the shell's and
// this row's own horizontal padding) so the "Size"/"Placed" label always has room too.
const SEGMENT_GROUP_CLASS_NAME = 'w-[160px]'

const SIZE_OPTIONS: ReadonlyArray<TrayFilterSegmentOption<BackgroundsSizeFilter>> = [
  { value: 'any', label: 'Any', ariaLabel: 'Any size' },
  { value: 'spread', label: 'Spread', ariaLabel: 'Spread' },
  { value: 'page', label: 'Page', ariaLabel: 'Page' },
]

type BackgroundsTrayFiltersProps = {
  /** False for Formula colors — plain swatches with no CMS keywords to search. */
  keywordsSearchable: boolean
  selectedKeywordIds: string[]
  onSelectedKeywordIdsChange: (keywordIds: string[]) => void
  onKeywordsOpenChange: (open: boolean) => void
  filenameQuery: string
  onFilenameQueryChange: (query: string) => void
  /** Matching filenames for the "Search by filename" dropdown. */
  filenameSuggestions: string[]
  onFilenameSuggestionsOpenChange: (open: boolean) => void
  sizeFilter: BackgroundsSizeFilter
  onSizeFilterChange: (size: BackgroundsSizeFilter) => void
  placedFilter: BackgroundsPlacedFilter
  onPlacedFilterChange: (placed: BackgroundsPlacedFilter) => void
}

/** Controls inside the Backgrounds tray's Filters section: keywords, filename, Size, Placed. */
export function BackgroundsTrayFilters({
  keywordsSearchable,
  selectedKeywordIds,
  onSelectedKeywordIdsChange,
  onKeywordsOpenChange,
  filenameQuery,
  onFilenameQueryChange,
  filenameSuggestions,
  onFilenameSuggestionsOpenChange,
  sizeFilter,
  onSizeFilterChange,
  placedFilter,
  onPlacedFilterChange,
}: BackgroundsTrayFiltersProps) {
  return (
    <>
      {keywordsSearchable ? (
        <div className="flex flex-col gap-3 px-4 pb-4 pt-2">
          <SearchBackgroundKeywordsCombobox
            value={selectedKeywordIds}
            onChange={onSelectedKeywordIdsChange}
            onOpenChange={onKeywordsOpenChange}
          />
        </div>
      ) : null}
      <div className={cn('px-4 pb-4', !keywordsSearchable && 'pt-2')}>
        <TraySearchSuggestInput
          value={filenameQuery}
          onChange={onFilenameQueryChange}
          placeholder="Search by filename"
          suggestions={filenameSuggestions}
          onOpenChange={onFilenameSuggestionsOpenChange}
        />
      </div>
      <div className="flex flex-col gap-3">
        <TrayFilterSegmentRow
          label="Size"
          value={sizeFilter}
          options={SIZE_OPTIONS}
          onChange={onSizeFilterChange}
          className="px-4"
          groupClassName={SEGMENT_GROUP_CLASS_NAME}
        />
        <TrayFilterSegmentRow
          label="Placed"
          value={placedFilter}
          options={TRI_STATE_FILTER_OPTIONS}
          onChange={onPlacedFilterChange}
          className="px-4"
          groupClassName={SEGMENT_GROUP_CLASS_NAME}
        />
      </div>
    </>
  )
}
